// ChipEditor.tsx · v12.10.11.1 (#1481 a pasted or typed {token} becomes a chip; lang on the editable element) · 2026-10-09
// v12.4.12 (#482): optional chipTest - an editor holding a JSON body chips only real tokens, not the object's own braces.
// v12.5.3 (#542): contentEditable substitutes U+00A0 for a typed leading space, so hand-indenting a JSON
// body wrote 18 non-breaking spaces into the VALUE. JSON.parse and System.Text.Json both reject U+00A0 as
// whitespace (Newtonsoft accepts it, which is why PM Web answered 200 and only our own gate objected), so
// the action could not be saved and 12.5.2's pre-send check refused to send it. The value is plain text by
// definition - a space the author typed is a space - so serialize() normalizes on the way OUT and the
// problem never reaches a stored value again.
// ChipEditor.tsx — v12.3.10.1 (#302)
// The prompt field renders every {token} as a CHIP carrying the tenant's word ("Visitor › Full name")
// while the value it stores is unchanged: the canonical token. That split is the whole point of
// BDR-001 v1.1 — rename a label and every prompt re-reads instantly, because nothing stored moved.
//
// WHY A contentEditable AND NOT A <textarea>: a textarea can only show the characters it holds, so the
// author had to read {subject.name} and translate it in their head. Reported on the 12.3.10 pass:
// "the picker, prompt display everything will always display label and not the token."
//
// HOW IT STAYS SAFE: the component's contract is still (value: string, onChange: (string) => void), so
// every consumer — Quick Answers, the workflow designer, the AI Agent page — is untouched. The caret is
// reported in VALUE SPACE (the offset into the token-bearing string, not into the DOM), so TokenPicker's
// existing `prefix.` autocomplete, its insert-at-caret and its drag-drop all keep working unchanged.
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export interface ChipEditorHandle {
  focus(): void;
  /** Caret position as an offset into the VALUE string (chips count as "{token}"). */
  getCaret(): number;
  /** Place the caret at a VALUE-string offset. */
  setCaret(pos: number): void;
  /** Caret coordinates relative to the editor's padding box, for anchoring the autocomplete list. */
  caretRect(): { top: number; left: number };
}

// v12.5.3 (#542): the space characters a browser puts in contentEditable that are NOT the space the author
// typed. U+00A0 is the one that bit us (leading indentation); U+202F and U+2007 arrive the same way from a
// paste out of Word or a PDF. A carriage return is legal JSON whitespace but is noise in a stored value.
const normalizeTyped = (s: string) => s.replace(/[\u00a0\u202f\u2007]/g, ' ').replace(/\r/g, '');

/** DOM -> value string. A chip is its token in braces; a <br> is a newline. */
function serialize(root: HTMLElement): string {
  let out = '';
  const walk = (n: Node) => {
    for (const c of Array.from(n.childNodes)) {
      if (c.nodeType === Node.TEXT_NODE) { out += normalizeTyped(c.nodeValue ?? ''); continue; }
      const el = c as HTMLElement;
      const tok = el.dataset?.token;
      if (tok) { out += '{' + tok + '}'; continue; }
      if (el.tagName === 'BR') { out += '\n'; continue; }
      // contentEditable wraps new lines in DIVs; each one starts a line.
      if (el.tagName === 'DIV' || el.tagName === 'P') { if (out && !out.endsWith('\n')) out += '\n'; }
      walk(el);
    }
  };
  walk(root);
  return out;
}

/** Caret as a VALUE-string offset: serialize everything BEFORE the caret (the same function that makes the value). */
function caretIn(el: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return serialize(el).length;
  const pre = document.createRange();
  pre.selectNodeContents(el);
  const r = sel.getRangeAt(0);
  pre.setEnd(r.endContainer, r.endOffset);
  const tmp = document.createElement('div');
  tmp.appendChild(pre.cloneContents());
  return serialize(tmp).length;
}

/** Place the caret at a VALUE-string offset (a chip counts as "{token}", a <br> as one character). */
function setCaretIn(el: HTMLElement, pos: number) {
  let acc = 0;
  const find = (n: Node): Range | null => {
    for (const c of Array.from(n.childNodes)) {
      if (c.nodeType === Node.TEXT_NODE) {
        const len = (c.nodeValue ?? '').length;
        if (acc + len >= pos) { const r = document.createRange(); r.setStart(c, Math.max(0, pos - acc)); r.collapse(true); return r; }
        acc += len; continue;
      }
      const e = c as HTMLElement;
      const tok = e.dataset?.token;
      if (tok) {
        const len = tok.length + 2;
        if (acc + len >= pos) { const r = document.createRange(); r.setStartAfter(e); r.collapse(true); return r; }
        acc += len; continue;
      }
      if (e.tagName === 'BR') {
        if (acc + 1 >= pos) { const r = document.createRange(); r.setStartAfter(e); r.collapse(true); return r; }
        acc += 1; continue;
      }
      const inner = find(e); if (inner) return inner;
    }
    return null;
  };
  let range = find(el);
  if (!range) { range = document.createRange(); range.selectNodeContents(el); range.collapse(false); }
  const sel = window.getSelection();
  sel?.removeAllRanges(); sel?.addRange(range);
  el.focus();
}

/** v12.10.11.1 (#1481): how many {…} in a value should be chips (chipTest decides, as render() does). */
function chipCount(value: string, chipTest?: (inner: string) => boolean): number {
  return (value.match(/\{[^{}]+\}/g) ?? []).filter((m) => !chipTest || chipTest(m.slice(1, -1))).length;
}

function makeChip(token: string, label?: string, known = true): HTMLSpanElement {
  const s = document.createElement('span');
  s.className = 'tok-chip' + (known ? '' : ' is-unknown');
  s.contentEditable = 'false';
  s.dataset.token = token;
  s.textContent = label || '{' + token + '}';
  // The raw token stays one hover away — authors still need to see it when debugging a prompt.
  s.title = known ? '{' + token + '}' : `{${token}} — not a token the engine can resolve`;
  return s;
}

/** value string -> DOM. */
function render(root: HTMLElement, value: string, labelOf?: (t: string) => string | undefined, isKnown?: (t: string) => boolean, chipTest?: (inner: string) => boolean) {
  root.textContent = '';
  for (const part of value.split(/(\{[^{}]+\})/g)) {
    if (!part) continue;
    const m = /^\{([^{}]+)\}$/.exec(part);
    // v12.4.12 (#482): an editor holding JSON can say which brace pairs are tokens, so `{"a": 1}` stays text.
    if (m && (!chipTest || chipTest(m[1]))) { root.appendChild(makeChip(m[1], labelOf?.(m[1]), isKnown ? isKnown(m[1]) : true)); continue; }
    part.split('\n').forEach((seg, i) => {
      if (i) root.appendChild(document.createElement('br'));
      if (seg) root.appendChild(document.createTextNode(seg));
    });
  }
}

export const ChipEditor = forwardRef<ChipEditorHandle, {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  labelOf?: (token: string) => string | undefined;
  isKnown?: (token: string) => boolean;
  chipTest?: (inner: string) => boolean;   // v12.4.12 (#482): which {…} become chips; default every one
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onCaretMove?: () => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  /** v12.10.11.1 (#1481): the language of the text (BCP-47, e.g. "es"), so the browser spell-checks Spanish as Spanish. */
  lang?: string;
}>(function ChipEditor(props, ref) {
  const rootRef = useRef<HTMLDivElement>(null);
  // What we last handed upward. The DOM is only rebuilt when the incoming value differs from this —
  // rebuilding on every keystroke would destroy the caret.
  const emitted = useRef<string>('');

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (props.value === emitted.current && el.childNodes.length) return;
    render(el, props.value, props.labelOf, props.isKnown, props.chipTest);
    emitted.current = props.value;
  }, [props.value, props.labelOf, props.isKnown, props.chipTest]);

  const emit = (composing = false) => {
    const el = rootRef.current; if (!el) return;
    const v = serialize(el);
    // v12.10.11.1 (#1481): a {token} that arrived as TEXT - pasted, or typed up to its closing brace - becomes a chip
    // exactly as Insert token makes one (an unknown one becomes the flagged chip). Before this the value held the token
    // but the DOM kept plain text, because the re-render only runs when the value CHANGES. The caret is kept in value
    // space, where a chip and its "{token}" text are the same length, so it lands where it was.
    if (!composing && chipCount(v, props.chipTest) !== el.querySelectorAll('[data-token]').length) {
      const focused = document.activeElement === el;
      const caret = focused ? caretIn(el) : -1;
      render(el, v, props.labelOf, props.isKnown, props.chipTest);
      if (focused) setCaretIn(el, caret);
    }
    emitted.current = v;
    props.onChange(v);
  };

  useImperativeHandle(ref, (): ChipEditorHandle => ({
    focus() { rootRef.current?.focus(); },

    getCaret() {
      const el = rootRef.current; if (!el) return 0;
      return caretIn(el);
    },

    setCaret(pos: number) {
      const el = rootRef.current; if (!el) return;
      setCaretIn(el, pos);
    },

    caretRect() {
      const el = rootRef.current;
      if (!el) return { top: 8, left: 10 };
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return { top: 8, left: 10 };
      const r = sel.getRangeAt(0).cloneRange();
      let rect = r.getBoundingClientRect();
      if (!rect || (rect.top === 0 && rect.left === 0)) {
        // A collapsed range at an element boundary has no rectangle — measure a zero-width marker.
        const probe = document.createElement('span');
        probe.textContent = '​';
        r.insertNode(probe);
        rect = probe.getBoundingClientRect();
        probe.remove();
      }
      const box = el.getBoundingClientRect();
      return {
        top: rect.top - box.top + rect.height + 2 - 0,
        left: Math.max(4, Math.min(rect.left - box.left, Math.max(4, el.clientWidth - 240))),
      };
    },
  }), []);

  return (
    <div
      ref={rootRef}
      className={`ah-text tok-chipeditor${props.disabled ? ' is-disabled' : ''}`}
      contentEditable={!props.disabled}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={props.placeholder}
      data-placeholder={props.placeholder}
      style={{ minHeight: `${Math.max(2, props.rows ?? 3) * 1.5 + 1}em` }}
      lang={props.lang}
      spellCheck
      onInput={(e) => emit((e.nativeEvent as InputEvent).isComposing === true)}
      onBlur={() => emit()}
      onKeyDown={props.onKeyDown}
      onKeyUp={props.onKeyUp}
      onClick={props.onCaretMove}
      onFocus={props.onCaretMove}
      onDragOver={(e) => e.preventDefault()}
      onDrop={props.onDrop}
      // Paste as PLAIN TEXT. A rich paste would inject spans and styles the serializer would have to
      // guess at, and the value is plain text by definition.
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const r = sel.getRangeAt(0);
        r.deleteContents();
        const frag = document.createDocumentFragment();
        text.split('\n').forEach((seg, i) => {
          if (i) frag.appendChild(document.createElement('br'));
          if (seg) frag.appendChild(document.createTextNode(seg));
        });
        const last = frag.lastChild;
        r.insertNode(frag);
        if (last) { const nr = document.createRange(); nr.setStartAfter(last); nr.collapse(true); sel.removeAllRanges(); sel.addRange(nr); }
        emit();
      }}
    />
  );
});
